import React, { CSSProperties, useState } from 'react';
import { FileImageOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Modal, Tooltip, Typography } from 'antd';

import PublicImageUpload from '../utils/PublicImageUpload';

const buttonStyle: CSSProperties = {
  alignItems: 'center',
  background: '#f8fafc',
  borderRadius: 8,
  color: '#bfbfbf',
  display: 'flex',
  height: 30,
  justifyContent: 'center',
  padding: 3,
  width: 80,
};

const imageStyle: CSSProperties = {
  maxHeight: 24,
  maxWidth: 72,
  objectFit: 'contain',
};

const pendingStyle: CSSProperties = {
  borderRadius: 4,
  outline: '2px solid #d4b106',
  outlineOffset: 1,
};

interface EditableDataChangeRequestLogoProps {
  currentLogoUrl?: string | null;
  disabled?: boolean;
  label: string;
  logoUrl?: string | null;
  onChange: (logoUrl: string | null) => void;
  onRevert?: () => void;
  pending?: boolean;
  style?: CSSProperties;
  uploadPath: string;
}

const EditableDataChangeRequestLogo: React.FC<EditableDataChangeRequestLogoProps> = ({
  currentLogoUrl,
  disabled = false,
  label,
  logoUrl,
  onChange,
  onRevert,
  pending = false,
  style,
  uploadPath,
}) => {
  const [open, setOpen] = useState(false);
  const [draftUrl, setDraftUrl] = useState<string | null>(null);

  const edit = () => {
    setDraftUrl(logoUrl || null);
    setOpen(true);
  };

  return (
    <div style={{ alignItems: 'center', display: 'flex', ...style }}>
      <Tooltip title="Change logo">
        <Button
          aria-label={`Change ${label} logo`}
          disabled={disabled}
          onClick={edit}
          style={{ ...buttonStyle, ...(pending ? pendingStyle : {}) }}
          type="dashed"
        >
          {logoUrl ? (
            <img alt="" src={logoUrl} style={imageStyle} />
          ) : (
            <FileImageOutlined />
          )}
        </Button>
      </Tooltip>
      {pending && onRevert && !disabled ? (
        <Tooltip
          title={`Revert to current value: ${
            currentLogoUrl ? 'Current logo' : 'No logo'
          }`}
        >
          <Button
            aria-label="Revert pending logo"
            icon={<UndoOutlined />}
            onClick={onRevert}
            size="small"
            style={{ marginLeft: 4 }}
            type="text"
          />
        </Tooltip>
      ) : null}
      <Modal
        destroyOnClose
        okButtonProps={{ disabled: draftUrl === (logoUrl || null) }}
        okText="Save"
        onCancel={() => setOpen(false)}
        onOk={() => {
          onChange(draftUrl);
          setOpen(false);
        }}
        title={`${label} logo`}
        visible={open}
      >
        <PublicImageUpload
          cropProps={{
            aspect: 480 / 180,
            cropperProps: { restrictPosition: false },
            minZoom: 0.5,
            rotate: true,
          }}
          maxFileSize={2 * 1024 * 1024}
          onChange={(upload) => setDraftUrl(upload.url || null)}
          previewStyle={{ height: 120, width: 320 }}
          targetHeight={180}
          targetWidth={480}
          uploadPath={`${uploadPath}/logos/${Date.now()}/`}
          value={{ url: draftUrl || undefined }}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
          Optimal size is 480 × 180px. Use a JPG or PNG smaller than 2MB.
        </Typography.Paragraph>
      </Modal>
    </div>
  );
};

export default EditableDataChangeRequestLogo;
